// Cola de subida de fotos a S3 con Storage.put (Amplify 5) y medidor de progreso y velocidad.
//
// S3 solo habla HTTP/1.1: el navegador abre 6 conexiones al bucket y encola el resto. Storage.put
// sube en una sola petición hasta 5 MiB y por encima en multipart, con hasta 4 partes a la vez.
// La cola reparte esas 6 conexiones según el tamaño real de cada foto (ya comprimida): ninguna
// petición espera turno en el navegador, así que una foto sin progreso está colgada de verdad.

export const PART_SIZE = 5 * 1024 * 1024; // DEFAULT_PART_SIZE de @aws-amplify/storage 5
export const PARTS_IN_FLIGHT = 4; // DEFAULT_QUEUE_SIZE de @aws-amplify/storage 5
export const CONNECTION_BUDGET = 6;

export const connectionsFor = (size) =>
  size <= PART_SIZE ? 1 : Math.min(Math.ceil(size / PART_SIZE), PARTS_IN_FLIGHT);

/**
 * Sube `items` con `upload(prepared, attempt, { signal, onProgress })` repartiendo `budget` conexiones.
 * - `prepare(item)` (p. ej. comprimir) se adelanta a la subida; la foto entra con su tamaño real.
 * - Se respeta el orden: si la primera no cabe, espera a que se libere sitio (una foto grande no se
 *   queda atrás para siempre); con la cola vacía entra aunque pida más que el presupuesto.
 * - Un fallo que `retryDelay(error, attempt)` acepta (devuelve ms, o Infinity para esperar a
 *   `subscribeWake`) vuelve al final de la cola y no ocupa sitio mientras espera, así que una foto
 *   colgada no frena a las demás.
 * - `subscribeWake(wake)` recibe una función que adelanta todos los reintentos en espera (p. ej. al
 *   volver la conexión) y devuelve la que cancela la suscripción.
 * - `upload` avisa con `onProgress(loaded)` de lo que lleva enviado y deja de subir cuando se aborta
 *   `signal`. Con eso la cola vigila el avance (si se pasan los plazos):
 *   - Una foto que lleva `stallMs` sin enviar nada mientras otras avanzan está colgada ella sola:
 *     se aborta y cuenta como un fallo más (`retryDelay` recibe un error con `stalled`).
 *   - Si todas las que están en curso llevan `quietMs` calladas, es la red la que no responde: se
 *     abortan, se quedan en cabeza sin gastar intento y se relanzan las mismas tras
 *     `holdDelay(ronda)` ms (Infinity espera a `subscribeWake`), sin que entre ninguna nueva.
 *     `onNetworkStall(true)` lo avisa. En cuanto cualquier petición avanza la red ha vuelto
 *     (`onNetworkStall(false)`): las que llevan `quietMs` calladas se relanzan ya.
 *   Cada foto abortada sin gastar intento pasa por `onHold`.
 * Resuelve cuando todas han subido o fallado del todo; nunca rechaza.
 */
export function runUploadQueue(items, {
  upload,
  prepare = async (item) => item,
  sizeOf = (prepared) => prepared.file.size,
  budget = CONNECTION_BUDGET,
  maxRetries = 3,
  retryDelay = () => false,
  prepareAhead = budget,
  stallMs = Infinity,
  quietMs = Infinity,
  holdDelay = () => 0,
  tickMs = 1000,
  onUploaded = () => {},
  onRetry = () => {},
  onFailed = () => {},
  onHold = () => {},
  onNetworkStall = () => {},
  subscribeWake = () => () => {},
  now = Date.now,
}) {
  const queue = items.map((item, index) => ({ item, index, attempt: 0, prepared: undefined, preparing: false, notBefore: 0 }));
  const running = new Set();
  let used = 0;
  let wakeTimer = null;
  let tickTimer = null;
  // Red sin respuesta: solo entran las retenidas (`held`), y no antes de `holdUntil`.
  let networkStalled = false;
  let holdRound = 0;
  let holdUntil = 0;

  return new Promise((resolve) => {
    const unsubscribe = subscribeWake(() => {
      queue.forEach((entry) => { entry.notBefore = 0; });
      fill();
    });
    const fill = () => {
      if (queue.length === 0 && running.size === 0) {
        clearTimeout(wakeTimer);
        clearInterval(tickTimer);
        unsubscribe();
        resolve();
        return;
      }
      // Preparar por delante, en orden, las siguientes que van a entrar.
      queue.slice(0, prepareAhead).forEach((entry) => {
        if (entry.preparing || entry.prepared) return;
        entry.preparing = true;
        prepare(entry.item).then(
          (prepared) => { entry.prepared = prepared; },
          (error) => { entry.prepareError = error; }
        ).finally(() => { entry.preparing = false; fill(); });
      });

      const t = now();
      let nextWake = Infinity;
      for (let i = 0; i < queue.length;) {
        const entry = queue[i];
        if (entry.prepareError !== undefined) {
          queue.splice(i, 1);
          onFailed(entry.item, entry.prepareError);
          continue;
        }
        // Sin red no entran fotos nuevas: solo se relanzan las que estaban en curso.
        if (networkStalled && !entry.held) break;
        // Las que esperan para reintentar no bloquean a las de detrás.
        if (entry.notBefore > t) { nextWake = Math.min(nextWake, entry.notBefore); i++; continue; }
        if (!entry.prepared) break;
        const connections = connectionsFor(sizeOf(entry.prepared));
        if (running.size > 0 && used + connections > budget) break;
        queue.splice(i, 1);
        start(entry, connections);
      }
      clearTimeout(wakeTimer);
      if (Number.isFinite(nextWake)) wakeTimer = setTimeout(fill, Math.max(0, nextWake - now()));
    };

    const stop = (entry, reason) => {
      entry.stopReason = reason;
      entry.controller.abort();
    };

    // Vuelve a cabeza de la cola, en su orden original entre las que también vuelven.
    const requeueFirst = (entry) => {
      entry.first = true;
      let i = 0;
      while (i < queue.length && queue[i].first && queue[i].index < entry.index) i++;
      queue.splice(i, 0, entry);
    };

    const recover = () => {
      networkStalled = false;
      holdRound = 0;
      onNetworkStall(false);
      const t = now();
      for (const entry of running) {
        if (entry.stopReason === "hold") entry.stopReason = "revive";
        else if (!entry.stopReason && t - entry.lastProgressAt >= quietMs) stop(entry, "revive");
      }
      queue.forEach((entry) => {
        if (entry.held) { entry.held = false; entry.notBefore = 0; }
      });
      fill();
    };

    const check = () => {
      const t = now();
      const live = [...running].filter((entry) => !entry.stopReason);
      if (live.length === 0) return;
      const quiet = (entry) => t - entry.lastProgressAt >= quietMs;
      if (live.every(quiet)) {
        if (!networkStalled) { networkStalled = true; onNetworkStall(true); }
        holdUntil = t + holdDelay(holdRound++);
        live.forEach((entry) => stop(entry, "hold"));
        return;
      }
      for (const entry of live) {
        if (t - entry.lastProgressAt >= stallMs) stop(entry, "stalled");
        // Relanzada mientras la red no respondía y sigue callada aunque otras ya avanzan.
        else if (entry.relaunched && quiet(entry)) stop(entry, "revive");
      }
    };
    if (Number.isFinite(stallMs) || Number.isFinite(quietMs)) tickTimer = setInterval(check, tickMs);

    const failed = (entry, error) => {
      const reason = entry.stopReason;
      if (reason === "hold" || reason === "revive") {
        entry.held = reason === "hold";
        entry.notBefore = entry.held ? holdUntil : 0;
        requeueFirst(entry);
        onHold(entry.prepared);
        return;
      }
      if (reason === "stalled") {
        error = Object.assign(new Error(`stalled ${stallMs / 1000} s`), { stalled: true, cause: error });
      }
      const delay = entry.attempt < maxRetries ? retryDelay(error, entry.attempt + 1) : false;
      if (delay === false || delay === undefined) {
        onFailed(entry.prepared, error);
        return;
      }
      entry.attempt++;
      entry.notBefore = now() + delay;
      queue.push(entry);
      onRetry(entry.prepared, entry.attempt, error);
    };

    const start = (entry, connections) => {
      const controller = new AbortController();
      Object.assign(entry, {
        controller, stopReason: undefined, loaded: 0, lastProgressAt: now(),
        relaunched: networkStalled, first: false,
      });
      used += connections;
      running.add(entry);
      const onProgress = (loaded) => {
        if (!(loaded > entry.loaded)) return;
        entry.loaded = loaded;
        entry.lastProgressAt = now();
        entry.relaunched = false;
        if (networkStalled) recover();
      };
      Promise.resolve()
        .then(() => upload(entry.prepared, entry.attempt, { signal: controller.signal, onProgress }))
        .then(
          (result) => {
            // Una subida terminada también demuestra que hay red.
            if (networkStalled) recover();
            onUploaded(entry.prepared, result);
          },
          (error) => failed(entry, error)
        )
        .finally(() => {
          used -= connections;
          running.delete(entry);
          fill();
        });
    };

    fill();
  });
}

/**
 * Progreso y velocidad de toda la subida.
 * - Progreso: cada foto pesa su tamaño original y avanza con la fracción subida de su fichero real
 *   (comprimido o no), así que fotos grandes y pequeñas cuentan lo que pesan. Un reintento no hace
 *   retroceder la barra: la foto se queda donde estaba hasta que el nuevo intento la supera.
 * - Velocidad: bytes enviados en los últimos `windowMs` de reloj. Con fotos en paralelo comparten
 *   el ancho de subida: sumar el tiempo de cada una daba una velocidad dividida entre las que iban
 *   a la vez. Los bytes de un intento cancelado también se enviaron y cuentan. Tras `idleMs` sin
 *   enviar nada es 0, sin esperar a que la ventana se vacíe.
 * - Racha: cuánto lleva enviando sin parones de `idleMs`.
 */
export class UploadMeter {
  constructor(items, { windowMs = 5000, idleMs = 3000, now = Date.now } = {}) {
    this.now = now;
    this.windowMs = windowMs;
    this.idleMs = idleMs;
    this.lastSentAt = now();
    this.weights = new Map(items.map(({ id, size }) => [id, size || 1]));
    this.totalWeight = [...this.weights.values()].reduce((a, b) => a + b, 0) || 1;
    this.fraction = new Map();
    this.loaded = new Map();
    this.lastProgressAt = new Map();
    this.sent = 0;
    this.streakStart = null;
    this.startedAt = now();
    this.samples = [[this.startedAt, 0]];
  }

  // Empieza un intento: cuenta lo que envíe desde cero.
  start(id) {
    this.loaded.set(id, 0);
    this.lastProgressAt.set(id, this.now());
  }

  progress(id, loaded, total) {
    const delta = loaded - (this.loaded.get(id) || 0);
    if (delta > 0) {
      // Tras un parón la ventana empieza de nuevo: la velocidad al reanudar no arrastra los ceros.
      if (this.now() - this.lastSentAt >= this.idleMs) this.samples = [[this.now(), this.sent]];
      if (this.streakStart === null || this.now() - this.lastSentAt >= this.idleMs) this.streakStart = this.now();
      this.sent += delta;
      this.lastSentAt = this.now();
      this.lastProgressAt.set(id, this.lastSentAt);
    }
    this.loaded.set(id, loaded);
    if (total > 0) this.fraction.set(id, Math.max(this.fraction.get(id) || 0, Math.min(1, loaded / total)));
  }

  // Subida o fallida del todo: deja de contar como activa.
  finish(id, uploaded) {
    if (uploaded) this.fraction.set(id, 1);
    this.lastProgressAt.delete(id);
  }

  percent() {
    let done = 0;
    for (const [id, weight] of this.weights) done += weight * (this.fraction.get(id) || 0);
    return Math.min(100, Math.floor((done / this.totalWeight) * 100));
  }

  // Mbps decimales (los de un test de velocidad o la limitación de DevTools).
  speedMbps() {
    const t = this.now();
    this.samples.push([t, this.sent]);
    while (this.samples.length > 2 && this.samples[1][0] <= t - this.windowMs) this.samples.shift();
    if (t - this.lastSentAt >= this.idleMs) return 0;
    const [t0, sent0] = this.samples[0];
    const seconds = (t - t0) / 1000;
    return seconds >= 0.5 ? ((this.sent - sent0) * 8) / seconds / 1e6 : 0;
  }

  // Milisegundos que lleva enviando sin parones; 0 si ahora no envía.
  steadyFor() {
    const t = this.now();
    if (this.streakStart === null || t - this.lastSentAt >= this.idleMs) return 0;
    return t - this.streakStart;
  }

  // Fotos en curso que llevan `ms` o más sin enviar un byte.
  stuck(ms) {
    const t = this.now();
    let count = 0;
    for (const at of this.lastProgressAt.values()) if (t - at >= ms) count++;
    return count;
  }
}

/**
 * Conexión lenta: velocidad media de los últimos `windowMs`, llamando a `update(sent)` con los bytes
 * enviados hasta ahora (p. ej. cada segundo). No opina hasta llevar `warmupMs` desde el primer byte
 * (el arranque y las primeras fotos dan cifras bajas engañosas). Pasa a lenta por debajo de
 * `slowBelowMbps` y no deja de serlo hasta llegar a `recoveredFromMbps`, para que no parpadee.
 */
export class SlowConnection {
  constructor({ windowMs = 30000, warmupMs = 30000, slowBelowMbps = 20, recoveredFromMbps = 25, now = Date.now } = {}) {
    Object.assign(this, { windowMs, warmupMs, slowBelowMbps, recoveredFromMbps, now });
    this.samples = [];
    this.firstSentAt = null;
    this.slow = false;
  }

  update(sent) {
    const t = this.now();
    if (sent > 0 && this.firstSentAt === null) this.firstSentAt = t;
    this.samples.push([t, sent]);
    while (this.samples.length > 2 && this.samples[1][0] <= t - this.windowMs) this.samples.shift();
    if (this.firstSentAt === null || t - this.firstSentAt < this.warmupMs) return this.slow;
    const [t0, sent0] = this.samples[0];
    const mbps = t > t0 ? ((sent - sent0) * 8) / ((t - t0) / 1000) / 1e6 : 0;
    if (!this.slow && mbps < this.slowBelowMbps) this.slow = true;
    else if (this.slow && mbps >= this.recoveredFromMbps) this.slow = false;
    return this.slow;
  }
}
